/**
 * Flammable Installation Types for MEV-Med Frontend
 * Instalações com inflamáveis (Laudo NR 20) — contrato da issue #366.
 *
 * O servidor calcula classe, categoria do produto, estado de cada item do prontuário e situação
 * (`summary`); a tela só mostra, e nenhuma regra é duplicada aqui. Datas viajam como texto
 * `YYYY-MM-DD`. O arquivo do registro nunca traz o caminho de gravação.
 */

export const FLAMMABLE_ACTIVITY_TYPES = [
  'POSTO_DE_SERVICO',
  'DISTRIBUICAO_CANALIZADA_ATE_18',
  'ENGARRAFADORA_DE_GAS',
  'TRANSPORTE_DUTOVIARIO',
  'DISTRIBUICAO_CANALIZADA_ACIMA_18',
  'REFINARIA',
  'PROCESSAMENTO_DE_GAS_NATURAL',
  'PETROQUIMICA',
  'USINA_DE_ETANOL',
  'ARMAZENAMENTO_OU_USO',
  'OUTRA',
] as const;
export type FlammableActivityType = (typeof FLAMMABLE_ACTIVITY_TYPES)[number];

export const FLAMMABLE_INSTALLATION_STATUSES = ['EM_OPERACAO', 'EM_MANUTENCAO', 'DESATIVADA'] as const;
export type FlammableInstallationStatus = (typeof FLAMMABLE_INSTALLATION_STATUSES)[number];

export const FLAMMABLE_PHYSICAL_STATES = ['GAS', 'LIQUIDO'] as const;
export type FlammablePhysicalState = (typeof FLAMMABLE_PHYSICAL_STATES)[number];

export const FLAMMABLE_RECORD_TYPES = [
  'PROJETO',
  'PLANO_INSPECAO_MANUTENCAO',
  'ANALISE_RISCOS',
  'PLANO_PREVENCAO_CONTROLE',
  'PLANO_RESPOSTA_EMERGENCIAS',
  'SIMULADO',
] as const;
export type FlammableRecordType = (typeof FLAMMABLE_RECORD_TYPES)[number];

export const FLAMMABLE_RECORD_METHODOLOGIES = [
  'APP_APR',
  'HAZOP',
  'E_SE',
  'FMEA',
  'ARVORE_DE_FALHAS',
  'OUTRA',
] as const;
export type FlammableRecordMethodology = (typeof FLAMMABLE_RECORD_METHODOLOGIES)[number];

export const FLAMMABLE_RECORD_RESULTS = ['SATISFATORIO', 'COM_FALHAS'] as const;
export type FlammableRecordResult = (typeof FLAMMABLE_RECORD_RESULTS)[number];

/** `ABAIXO_DO_LIMITE` é o rótulo "Abaixo do limite de classificação" (D18). */
export type FlammableClass = 'I' | 'II' | 'III' | 'ANEXO_II' | 'ABAIXO_DO_LIMITE' | 'SEM_DADOS';

export type FlammableTabelaClass = 'I' | 'II' | 'III';

export type FlammableProductCategory =
  | 'GAS_INFLAMAVEL'
  | 'LIQUIDO_INFLAMAVEL'
  | 'LIQUIDO_INFLAMAVEL_EQUIPARADO'
  | 'LIQUIDO_COMBUSTIVEL'
  | 'NAO_COMPUTA';

export type FlammableClassReason =
  | 'ATIVIDADE'
  | 'CAPACIDADE_GAS'
  | 'CAPACIDADE_LIQUIDOS'
  | 'MAIOR_GRADACAO'
  | 'SEM_DADOS';

export type FlammableItemState = 'EM_DIA' | 'VENCIDO' | 'AUSENTE' | 'NAO_EXIGIDO';

/** Situação calculada pelo servidor; nunca é gravada. */
export type FlammableSituation = 'DESATIVADA' | 'ABAIXO_DO_LIMITE' | 'EM_CADASTRO' | 'EM_DIA' | 'ATENCAO';

export const ROTULO_DA_ATIVIDADE: Readonly<Record<FlammableActivityType, string>> = {
  POSTO_DE_SERVICO: 'Posto de serviço com inflamáveis ou combustíveis',
  DISTRIBUICAO_CANALIZADA_ATE_18: 'Distribuição canalizada de gás, PMTA até 18 kgf/cm²',
  ENGARRAFADORA_DE_GAS: 'Engarrafadora de gases inflamáveis',
  TRANSPORTE_DUTOVIARIO: 'Transporte por duto de gases ou líquidos',
  DISTRIBUICAO_CANALIZADA_ACIMA_18: 'Distribuição canalizada de gás, PMTA acima de 18 kgf/cm²',
  REFINARIA: 'Refinaria',
  PROCESSAMENTO_DE_GAS_NATURAL: 'Unidade de processamento de gás natural',
  PETROQUIMICA: 'Instalação petroquímica',
  USINA_DE_ETANOL: 'Usina de fabricação de etanol',
  ARMAZENAMENTO_OU_USO: 'Armazenamento ou uso na própria operação',
  OUTRA: 'Outra (descrever)',
};

export const ROTULO_DO_STATUS_DA_INSTALACAO: Readonly<Record<FlammableInstallationStatus, string>> = {
  EM_OPERACAO: 'Em operação',
  EM_MANUTENCAO: 'Em manutenção',
  DESATIVADA: 'Desativada',
};

export const ROTULO_DO_ESTADO_FISICO: Readonly<Record<FlammablePhysicalState, string>> = {
  GAS: 'Gás inflamável',
  LIQUIDO: 'Líquido',
};

export const ROTULO_DA_CLASSE: Readonly<Record<FlammableClass, string>> = {
  I: 'Classe I',
  II: 'Classe II',
  III: 'Classe III',
  ANEXO_II: 'Exceção do Anexo II',
  ABAIXO_DO_LIMITE: 'Abaixo do limite de classificação',
  SEM_DADOS: 'Sem dados para classificar',
};

export const ROTULO_DA_CATEGORIA_DO_PRODUTO: Readonly<Record<FlammableProductCategory, string>> = {
  GAS_INFLAMAVEL: 'Gás inflamável',
  LIQUIDO_INFLAMAVEL: 'Líquido inflamável',
  LIQUIDO_INFLAMAVEL_EQUIPARADO: 'Líquido inflamável (equiparado)',
  LIQUIDO_COMBUSTIVEL: 'Líquido combustível',
  NAO_COMPUTA: 'Não computa na classe',
};

export const ROTULO_DO_TIPO_DE_REGISTRO: Readonly<Record<FlammableRecordType, string>> = {
  PROJETO: 'Projeto',
  PLANO_INSPECAO_MANUTENCAO: 'Plano de inspeção e manutenção',
  ANALISE_RISCOS: 'Análise de riscos',
  PLANO_PREVENCAO_CONTROLE: 'Plano de prevenção e controle',
  PLANO_RESPOSTA_EMERGENCIAS: 'Plano de resposta a emergências',
  SIMULADO: 'Simulado',
};

export const ROTULO_DA_METODOLOGIA: Readonly<Record<FlammableRecordMethodology, string>> = {
  APP_APR: 'APP/APR',
  HAZOP: 'HAZOP',
  E_SE: 'E se (What if)',
  FMEA: 'FMEA',
  ARVORE_DE_FALHAS: 'Árvore de falhas',
  OUTRA: 'Outra',
};

export const ROTULO_DO_RESULTADO: Readonly<Record<FlammableRecordResult, string>> = {
  SATISFATORIO: 'Satisfatório',
  COM_FALHAS: 'Com falhas',
};

export const ROTULO_DO_ESTADO_DO_ITEM: Readonly<Record<FlammableItemState, string>> = {
  EM_DIA: 'Em dia',
  VENCIDO: 'Vencido',
  AUSENTE: 'Ausente',
  NAO_EXIGIDO: 'Não exigido',
};

export const ROTULO_DA_SITUACAO_DA_INSTALACAO: Readonly<Record<FlammableSituation, string>> = {
  DESATIVADA: 'Desativada',
  ABAIXO_DO_LIMITE: 'Abaixo do limite',
  EM_CADASTRO: 'Em cadastro',
  EM_DIA: 'Em dia',
  ATENCAO: 'Atenção',
};

/** Produto armazenado. `capacity` é em toneladas (`GAS`) ou m³ (`LIQUIDO`). */
export interface FlammableSubstance {
  id: number;
  installationId: number;
  productName: string;
  physicalState: FlammablePhysicalState;
  /** Obrigatório em `LIQUIDO`; vazio em `GAS`. */
  flashPointCelsius: number | null;
  storedHeated: boolean;
  capacity: number;
  storageLocation: string | null;
  sdsAvailable: boolean;
  observations: string | null;
  /** Calculada pelo servidor. */
  category?: FlammableProductCategory;
}

export interface FlammableRecord {
  id: number;
  installationId: number;
  recordType: FlammableRecordType;
  documentedAt: string;
  responsible: string;
  responsibleRegistration: string | null;
  location: string | null;
  /** Vazia em `SIMULADO`: o servidor calcula 12 meses. */
  nextReviewDate: string | null;
  /** Obrigatória em `ANALISE_RISCOS`. */
  methodology: FlammableRecordMethodology | null;
  /** Obrigatório em `SIMULADO`. */
  result: FlammableRecordResult | null;
  observations: string | null;
  /** Arquivo anexado: o nome e o tipo vêm, o caminho de gravação nunca. */
  originalFileName?: string | null;
  fileMimeType?: string | null;
  fileSize?: number | null;
}

/** Último registro de um tipo, como o resumo o devolve. */
export interface FlammableLastRecord {
  id: number | string;
  recordType: FlammableRecordType;
  documentedAt: string;
  nextReviewDate: string | null;
}

export interface FlammableClassification {
  class: FlammableClass;
  reason: FlammableClassReason;
  classByCapacity: FlammableClass;
  classByActivity: FlammableTabelaClass | null;
  /** A classe pela atividade ficou abaixo da que a capacidade pediria. */
  activityBelowCapacity: boolean;
}

export interface FlammableCapacities {
  /** Milésimos de tonelada. */
  gasMilesimos: number;
  /** Milésimos de m³. */
  liquidosMilesimos: number;
}

export interface FlammableItemSummary {
  recordType: FlammableRecordType;
  required: boolean;
  state: FlammableItemState;
  last: FlammableLastRecord | null;
  nextReview: string | null;
}

/** Resumo calculado pelo servidor; a tela só mostra. */
export interface FlammableInstallationSummary {
  classification: FlammableClassification;
  capacities: FlammableCapacities;
  substanceCount: number;
  items: FlammableItemSummary[];
  requiredCount: number;
  upToDateCount: number;
  overdueCount: number;
  missingCount: number;
  nextDueDate: string | null;
  situation: FlammableSituation;
}

export interface FlammableInstallation {
  id: number;
  companyId: number;
  departmentId: number | null;
  name: string;
  tag: string | null;
  activityType: FlammableActivityType;
  /** Obrigatória quando a atividade é `OUTRA`. */
  activityDescription: string | null;
  status: FlammableInstallationStatus;
  location: string | null;
  observations: string | null;
  summary: FlammableInstallationSummary;
  substances?: FlammableSubstance[];
  records?: FlammableRecord[];
}

export interface FlammableInstallationFilters {
  search?: string;
  departmentId?: number;
  class?: FlammableClass;
  situation?: FlammableSituation;
}

export interface FlammableInstallationListResponse {
  installations: FlammableInstallation[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type FlammableInstallationFormData = Pick<FlammableInstallation, 'name' | 'activityType'> &
  Partial<
    Omit<
      FlammableInstallation,
      'id' | 'companyId' | 'name' | 'activityType' | 'summary' | 'substances' | 'records'
    >
  >;

/** Criação: quem não tem empresa ativa (plataforma, clínica) diz a empresa no corpo. */
export type FlammableInstallationCreateData = FlammableInstallationFormData & { companyId?: number };

export type FlammableSubstanceFormData = Pick<
  FlammableSubstance,
  'productName' | 'physicalState' | 'capacity'
> &
  Partial<
    Pick<
      FlammableSubstance,
      'flashPointCelsius' | 'storedHeated' | 'storageLocation' | 'sdsAvailable' | 'observations'
    >
  >;

export type FlammableRecordFormData = Pick<FlammableRecord, 'recordType' | 'documentedAt' | 'responsible'> &
  Partial<
    Pick<
      FlammableRecord,
      'responsibleRegistration' | 'location' | 'nextReviewDate' | 'methodology' | 'result' | 'observations'
    >
  >;
